/**
 * Spec 055, step 3: the «Набор» tab of a unit — the notes, skills and safe rights every card of
 * the unit gets. What the units above give is listed apart, in grey, with a link to them. People
 * of the unit save a new version; it changes nothing until any one of the top unit's people
 * approves it. The risky rights never enter a kit: they stay a card's alone.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import { Projects } from "@/api/resources";
import type { AgentAction } from "@/api/types";
import { UnitKits, type KitNoteGrant, type KitOrders, type KitOut, type KitRadar, type UnitKit } from "@/api/units";
import { Tick } from "@/components/AgentForm";
import { KIT_VERDICTS, NoteSearch, SkillSearch } from "@/components/GrantSearch";
import { Button } from "@/components/ui/Button";
import { Segmented } from "@/components/ui/Segmented";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { Caption, EmptyNote } from "@/kit";
import { ACTION_LABEL, ACTIONS } from "@/lib/agents";
import { INBOX_PROJECT_ID } from "@/lib/limits";

const RADAR: { value: KitRadar; label: string }[] = [
  { value: "none", label: "нет" },
  { value: "read", label: "читать" },
  { value: "report", label: "сообщать" },
];

const ORDERS: { value: KitOrders; label: string }[] = [
  { value: "none", label: "нет" },
  { value: "own", label: "свои" },
];

const NOTE_LEVELS: { value: "read" | "append"; label: string }[] = [
  { value: "read", label: "читать" },
  { value: "append", label: "дописывать" },
];

/** A kit in people's words, a grant a line — how the units above are shown. */
export function kitWords(kit: UnitKit, notes: Record<string, string>, projects: Record<string, string>): string[] {
  const lines = kit.notes.map((g) => `Заметка «${notes[g.note_id] || "без названия"}»: ${g.append ? "дописывать" : "читать"}`);
  lines.push(...kit.skills.map((slug) => `Навык ${slug}`));
  lines.push(...ACTIONS.filter((a) => kit.actions.includes(a)).map((a) => `Действие: ${ACTION_LABEL[a]}`));
  lines.push(...kit.projects.map((id) => `Проект «${projects[id] || "без названия"}»`));
  if (kit.radar !== "none") lines.push(`Находки: ${kit.radar === "read" ? "читать" : "сообщать"}`);
  if (kit.orders !== "none") lines.push("Заказы клиентов: свои");
  return lines;
}

/** The header line: the latest version, whether it is in force, and how many cards get it. */
export function kitState(kit: KitOut): string {
  if (!kit.version) return "Набора ещё нет";
  const state = kit.pending
    ? `ждёт одобрения, ${kit.approved_version ? `действует версия ${kit.approved_version}` : "набора ещё нет"}`
    : "одобрена";
  return `Версия ${kit.version} — ${state} · получат набор: ${kit.agents}`;
}

export function UnitKitTab({ unitId, live }: { unitId: string; live: boolean }) {
  const kit = useQuery({ queryKey: ["units", "kit", unitId], queryFn: () => UnitKits.get(unitId) });
  if (kit.isLoading) return <SkeletonList count={3} />;
  if (!kit.data) {
    return (
      <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void kit.refetch()}>Повторить</Button>}>
        Не удалось загрузить набор отдела.
      </EmptyNote>
    );
  }
  return <KitEditor key={kit.data.version} kit={kit.data} live={live} />;
}

export function KitEditor({ kit, live }: { kit: KitOut; live: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const projects = useQuery({ queryKey: ["projects"], queryFn: () => Projects.list(), enabled: live });
  const [notes, setNotes] = useState<KitNoteGrant[]>(kit.own.notes);
  const [titles, setTitles] = useState<Record<string, string>>(kit.note_titles ?? {});
  const [skills, setSkills] = useState<string[]>(kit.own.skills);
  const [actions, setActions] = useState<AgentAction[]>(kit.own.actions);
  const [chosen, setChosen] = useState<string[]>(kit.own.projects);
  const [radar, setRadar] = useState<KitRadar>(kit.own.radar);
  const [orders, setOrders] = useState<KitOrders>(kit.own.orders);
  const [adding, setAdding] = useState<"notes" | "skills" | null>(null);
  const draft: UnitKit = { notes, skills, actions, projects: chosen, radar, orders };
  const changed = JSON.stringify(draft) !== JSON.stringify(kit.own);
  const toggle = <T,>(list: T[], item: T) => (list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);

  const refused = (e: Error) => {
    toast.error(e.message);
    if (e instanceof ApiError && e.status === 409) void qc.invalidateQueries({ queryKey: ["units"] });
  };
  const save = useMutation({
    mutationFn: () => UnitKits.set(kit.unit_id, { ...draft, version: kit.version }),
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: ["units"] });
      toast.success(`Версия ${saved.version} сохранена — ждёт одобрения`);
    },
    onError: refused,
  });
  const approve = useMutation({
    mutationFn: () => UnitKits.approve(kit.unit_id, kit.version),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["units"] });
      void qc.invalidateQueries({ queryKey: ["agents"] });
      toast.success("Набор отдела одобрен: сотрудники получат его со следующего запуска");
    },
    onError: refused,
  });

  return (
    <div className="flex flex-col gap-4" data-unit-kit>
      <div className="flex flex-col gap-2">
        <p className="text-body text-p-text">{kitState(kit)}</p>
        {kit.pending && live && (
          <Button size="compact" variant="secondary" loading={approve.isPending} onClick={() => approve.mutate()}>
            Одобрить версию {kit.version}
          </Button>
        )}
        <p className="text-meta text-p-text-muted">
          Что здесь — получает каждый сотрудник отдела и вложенных отделов. Убрать лишнее одному агенту можно в его настройках.
          Рискованные права (чат с клиентом, суммы, сеть, «во всех проектах» и другие) в набор не входят — только в карточке агента.
        </p>
      </div>

      <section className="flex flex-col gap-2" aria-label="Заметки набора">
        <Caption>Заметки</Caption>
        {notes.map((g) => (
          <div key={g.note_id} className="flex flex-wrap items-center justify-between gap-2">
            <span className="min-w-0 flex-1 break-words text-body text-p-text">{titles[g.note_id] || "без названия"}</span>
            <Segmented options={NOTE_LEVELS} value={g.append ? "append" : "read"} label={`Заметка «${titles[g.note_id] || "без названия"}»`}
              onChange={(v) => setNotes(notes.map((x) => (x.note_id === g.note_id ? { ...x, append: v === "append" } : x)))} />
            {live && <Button size="compact" variant="ghost" onClick={() => setNotes(notes.filter((x) => x.note_id !== g.note_id))}>Убрать</Button>}
          </div>
        ))}
        {!notes.length && <span className="text-meta text-p-text-muted">Заметок нет.</span>}
        {live && (adding === "notes" ? (
          <NoteSearch taken={notes.map((g) => g.note_id)} onPick={(note) => {
            setTitles({ ...titles, [note.id]: note.title });
            setNotes([...notes, { note_id: note.id, append: false }]);
            setAdding(null);
          }} />
        ) : (
          <Button size="compact" variant="secondary" onClick={() => setAdding("notes")}>+ Добавить заметку</Button>
        ))}
      </section>

      <section className="flex flex-col gap-2" aria-label="Навыки набора">
        <Caption>Навыки</Caption>
        {skills.map((slug) => (
          <div key={slug} className="flex items-center justify-between gap-2">
            <span className="min-w-0 flex-1 break-words text-body text-p-text">{slug}</span>
            {live && <Button size="compact" variant="ghost" onClick={() => setSkills(skills.filter((x) => x !== slug))}>Убрать</Button>}
          </div>
        ))}
        {!skills.length && <span className="text-meta text-p-text-muted">Навыков нет.</span>}
        {live && (adding === "skills" ? (
          <SkillSearch taken={skills} allowed={KIT_VERDICTS} onPick={(skill) => {
            setSkills([...skills, skill.slug]);
            setAdding(null);
          }} />
        ) : (
          <Button size="compact" variant="secondary" onClick={() => setAdding("skills")}>+ Добавить навык</Button>
        ))}
        <span className="text-micro text-p-text-muted">В набор идут только навыки, проверенные SkillSpector. «Осторожно» разрешают в карточке агента.</span>
      </section>

      <section className="flex flex-col gap-2" aria-label="Права набора">
        <Caption>Права</Caption>
        <div className="flex flex-col gap-1">
          {ACTIONS.map((a) => (
            <Tick key={a} label={ACTION_LABEL[a]} on={actions.includes(a)} disabled={!live} onChange={() => setActions(toggle(actions, a))} />
          ))}
        </div>
        <div className="flex flex-col gap-1 pt-1">
          <span className="text-meta text-p-text-muted">Проекты</span>
          {(projects.data ?? []).filter((p) => !p.archived_at && p.id !== INBOX_PROJECT_ID).map((p) => (
            <Tick key={p.id} label={p.title} on={chosen.includes(p.id)} disabled={!live} onChange={() => setChosen(toggle(chosen, p.id))} />
          ))}
        </div>
        <Segmented options={RADAR} value={radar} onChange={setRadar} label="Находки" />
        <Segmented options={ORDERS} value={orders} onChange={setOrders} label="Заказы клиентов" />
      </section>

      {kit.inherited.map((parent) => (
        <section key={parent.unit_id} className="flex flex-col gap-1 rounded-card border border-p-border bg-p-surface-2 px-4 py-3 text-p-text-muted"
          aria-label={`Набор «${parent.unit_name}»`}>
          <span className="text-meta">
            От «{parent.unit_name}», версия {parent.version} ·{" "}
            <Link to={`/departments/${parent.unit_id}?tab=kit`} className="font-semibold text-p-link">открыть</Link>
          </span>
          <ul className="space-y-0.5 text-meta">
            {kitWords(parent.kit, kit.note_titles ?? {}, kit.project_titles ?? {}).map((line) => <li key={line}>· {line}</li>)}
          </ul>
        </section>
      ))}

      {live && (
        <Button block variant="strong" loading={save.isPending} disabled={!changed} onClick={() => save.mutate()}>
          Сохранить новую версию
        </Button>
      )}
    </div>
  );
}
